/**
 * Real-calendar-date validation for `date` and `date_partial` fields.
 *
 * The pattern rules in `fromSchema.ts` only guarantee the SHAPE of a value
 * (YYYY-MM-DD, YYYY-MM, YYYY). They accept shapes that are not real dates —
 * e.g. `1990-13-45` (month 13, day 45) or `2021-02-30` (Feb has no 30th).
 * These helpers reject those, and are shared by the live RHF `validate` rule
 * (on-blur error) and the whole-form `validateForm` submit gate so the two
 * cannot disagree about what counts as a valid date.
 */

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function isValidYmd(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12) return false;
  let maxDay = DAYS_IN_MONTH[month - 1];
  if (month === 2 && isLeapYear(year)) maxDay = 29;
  return day >= 1 && day <= maxDay;
}

/**
 * True only for a well-formed AND real YYYY-MM-DD calendar date.
 * Returns false for malformed shapes and impossible dates.
 */
export function isValidFullDate(value: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return false;
  return isValidYmd(Number(m[1]), Number(m[2]), Number(m[3]));
}

/**
 * True only for a well-formed AND real partial date: YYYY, YYYY-MM, or
 * YYYY-MM-DD. Month (when present) must be 01-12; day (when present) must be
 * valid for the month/year.
 */
export function isValidPartialDate(value: string): boolean {
  if (/^\d{4}$/.test(value)) return true;
  const ym = /^(\d{4})-(\d{2})$/.exec(value);
  if (ym) {
    const month = Number(ym[2]);
    return month >= 1 && month <= 12;
  }
  return isValidFullDate(value);
}

/** Dispatch on field type: partial dates use the looser partial check. */
export function isValidDateForType(value: string, type: 'date' | 'date_partial'): boolean {
  return type === 'date_partial' ? isValidPartialDate(value) : isValidFullDate(value);
}
